import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
// Featured Local Businesses: admin-dashboard + editor styles, kept in their own
// stylesheet so this feature does not have to be appended into the very large
// index.css.
import './business-admin.css';

// ⭐ Trusted Types default policy (Track F).
// The hosting CSP sets `require-trusted-types-for 'script'`. A few safe,
// app-controlled DOM assignments (e.g. MapPage marker innerHTML) must be
// routed through this default policy - it returns the input unchanged, so
// behavior is identical while satisfying the Trusted Types requirement.
declare global {
  interface Window {
    trustedTypes?: {
      createPolicy: (name: string, rules: {
        createHTML?: (s: string) => string;
        createScriptURL?: (s: string) => string;
      }) => unknown;
    };
  }
}

if (window.trustedTypes && window.trustedTypes.createPolicy) {
  try {
    window.trustedTypes.createPolicy('default', {
      createHTML: (s: string) => s,
      // ⭐ FIX (2026): `require-trusted-types-for 'script'` also blocks
      // navigator.serviceWorker.register(url) unless a createScriptURL
      // policy exists. Returning the URL unchanged keeps behavior identical
      // while satisfying TrustedScriptURL.
      createScriptURL: (s: string) => s,
    });
  } catch (e) {
    // Already exists or unsupported - never block the app on Trusted Types.
    console.warn('[SQ] Trusted Types default policy skipped:', e);
  }
}

// ══════════════════════════════════════════════
// Sentry Error Tracking
// ══════════════════════════════════════════════
// ⭐ Performance (Lighthouse certification pass): Sentry.init was running
// synchronously at module load, blocking first paint on the mobile critical
// path (~48% of the main chunk is unused on first view). Deferring init to
// after `window.load` keeps identical error/replay/tracing coverage (it
// registers the same DSN + integrations), just milliseconds later - it does
// NOT reduce Sentry capture reliability.
const SENTRY_DSN = 'https://66763b06687bb19bd228251032123ba1@o4511731233914880.ingest.us.sentry.io/4511731240009728';

// ⭐ MEASURED AND REJECTED (2026 performance pass): converting this to a
// dynamic `import('@sentry/react')` was built, measured and reverted. It did
// move the Sentry code off the critical path (-146 KiB raw / -49 KiB gzip on
// the first-load JS graph), but Rollup cannot tree-shake a dynamically
// imported SDK the way it can when the SDK is statically bundled: the deferred
// chunk measured 492 KiB / 162.6 KiB gzip, against the 146 KiB / 49 KiB gzip
// the same code costs inside the entry chunk. That is ~113 KiB gzip of EXTRA
// transfer downloaded straight after `window.load` - inside Lighthouse's TBT
// window and counted by the total-byte-weight audit - to buy 49 KiB gzip off
// LCP. Net negative, so the static import stays and only init() is deferred
// (which is what actually blocked first paint).
if (SENTRY_DSN) {
  const initSentry = () => {
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: 'production',
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.05,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
    });
  };
  if (document.readyState === 'complete') {
    initSentry();
  } else {
    window.addEventListener('load', initSentry, { once: true });
  }
}

// ══════════════════════════════════════════════
// Service Worker - single merged worker v4
// (FCM push + Workbox offline cache in ONE file)
//
// The old setup registered TWO workers at the same root scope ("/") - 
// /sw.js (Workbox) and /firebase-messaging-sw.js (FCM). They fought for
// control of the page, which broke push delivery and duplicated caches.
// Now only /firebase-messaging-sw.js is registered; the old /sw.js is
// unregistered and removed from the project.
// ══════════════════════════════════════════════
if ('serviceWorker' in navigator) {
  window.addEventListener('load', async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs
        .filter((r) => !r.active || !(r.active.scriptURL || '').includes('/firebase-messaging-sw.js'))
        .map((r) => r.unregister()));
      const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
      console.log('[SQ] SW v4 registered');
      await navigator.serviceWorker.ready;
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    } catch (err: any) {
      console.warn('[SQ] SW registration skipped:', err?.message);
    }
  });
}

// ⭐ Open Web Discovery: the built index.html carries crawlable semantic
// content inside <div id="root"> (build-time static shell - see
// scripts/site/site-gen.mjs). React would warn "container already has
// content" if it kept those children, so we clear them first. Users see
// the identical app (the splash overlay is already inlined in HTML);
// crawlers and non-JS readers get the static content unchanged.
const appContainer = document.getElementById('root');
if (appContainer && appContainer.firstChild) {
  appContainer.replaceChildren();
}

ReactDOM.createRoot(appContainer!).render(
  <React.StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </React.StrictMode>
);

// Track C: splash overlay (CSS fallback) must never cover the app.
// Fade out on first React commit, then remove from the DOM so it can
// never trap clicks or paint over subsequent route changes.
window.requestAnimationFrame(() => {
  const splash = document.getElementById('splash-overlay');
  if (!splash) return;
  splash.classList.add('splash-hide');
  window.setTimeout(() => {
    splash.remove();
  }, 400);
});
